"use client";

import {
  useEffect,
  useId,
  useRef,
  type KeyboardEvent,
  type ReactNode,
} from "react";

const scrollLocks = new Set<symbol>();
let savedOverflow = "";
export function lockBodyScroll() {
  const lock = Symbol("dialog");
  if (scrollLocks.size === 0) savedOverflow = document.body.style.overflow;
  scrollLocks.add(lock);
  document.body.style.overflow = "hidden";
  return () => {
    if (!scrollLocks.delete(lock)) return;
    if (scrollLocks.size === 0) document.body.style.overflow = savedOverflow;
  };
}

// Keep keyboard focus inside the modal, including at the native dialog's
// document boundary, where Chromium can otherwise focus the browser chrome.
export function trapDialogFocus(event: KeyboardEvent<HTMLDialogElement>) {
  if (event.key !== "Tab") return;
  const controls = Array.from(
    event.currentTarget.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ),
  ).filter((control) => control.getClientRects().length > 0);
  const first = controls[0];
  const last = controls[controls.length - 1];
  if (!first || !last) {
    event.preventDefault();
    return;
  }
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

export function Dialog({
  open,
  onClose,
  title,
  children,
  className = "",
  dismissible = true,
  variant = "modal",
  closeLabel = "Close modal",
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  className?: string;
  dismissible?: boolean;
  variant?: "modal" | "drawer";
  closeLabel?: string;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const unlock = useRef<(() => void) | null>(null);
  const id = useId();
  useEffect(() => {
    const dialog = ref.current;
    if (!open) {
      dialog?.close();
      return;
    }
    dialog?.showModal();
    const release = lockBodyScroll();
    unlock.current = release;
    return release;
  }, [open]);
  return (
    <dialog
      ref={ref}
      className={`dx-dialog dx-dialog--${variant} ${className}`}
      aria-labelledby={id}
      onCancel={(event) => { if (!dismissible) event.preventDefault(); }}
      onClick={(event) => {
        if (!dismissible || event.target !== event.currentTarget) return;
        const bounds = event.currentTarget.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) event.currentTarget.close();
      }}
      onClose={() => {
        unlock.current?.();
        onClose();
      }}
      onKeyDown={trapDialogFocus}
    >
      <div className="dx-dialog-heading">
        <h2 id={id}>{title}</h2>
        {dismissible && <button
          type="button"
          aria-label={closeLabel}
          onClick={() => ref.current?.close()}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 256 256"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z" />
          </svg>
        </button>}
      </div>
      {children}
    </dialog>
  );
}
